import { c as _c } from "react/compiler-runtime";
import { COUNTRIES } from '@onefootprint/core';
import { patchHostedIdentifySessionVault } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/request';
import type { VerifiedPrefillContext } from '@onefootprint/request-types';
import { Divider } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import noop from 'lodash/noop';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { checkIsPhoneValid } from '@/idv/utils';
import { getLogger } from '@/idv/utils/logger';
import { useL10nContext } from '../../../l10n-provider';
import type { Context } from '../../identify.types';
import getHeader from '../../utils/get-header';
import PhonePageStructure from '../phone-page-structure';
import { PoweredByVerified, useMarkConsentMutation, VerifiedPrefillDisclaimer } from '../verified-prefill-consent';
const {
  logInfo
} = getLogger({
  location: 'collect-phone'
});
type CollectPhoneProps = {
  defaultValues: {
    phoneNumber?: string;
  };
  context: Context;
  onPrev?: () => void;
  onDone: (phoneNumber: string) => Promise<void>;
  onError: (location: string, error: unknown) => void;
  children?: JSX.Element;
};
const CollectPhone = t0 => {
  const $ = _c(59);
  const {
    context,
    defaultValues,
    onPrev,
    onDone,
    onError,
    children
  } = t0;
  const {
    t
  } = useTranslation("identify");
  const {
    state: t1,
    initArgs: t2
  } = context;
  const {
    identifyToken,
    email,
    verifiedPrefillContext
  } = t1;
  const {
    isLive,
    logoConfig
  } = t2;
  const {
    getErrorContext,
    getErrorCode
  } = useRequestError();
  const markConsentMutation = useMarkConsentMutation(identifyToken, verifiedPrefillContext);
  let t3;
  if ($[0] !== identifyToken || $[1] !== markConsentMutation || $[2] !== onDone) {
    t3 = async phone => {
      const {
        data
      } = await patchHostedIdentifySessionVault({
        headers: {
          "X-Fp-Authorization": identifyToken
        },
        body: {
          "id.phone_number": phone
        }
      });
      await markConsentMutation.mutateAsync();
      if (phone) {
        await onDone(phone);
      }
      return data;
    };
    $[0] = identifyToken;
    $[1] = markConsentMutation;
    $[2] = onDone;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== getErrorCode || $[5] !== getErrorContext || $[6] !== onError) {
    t4 = error => {
      const isVaultDataValidationError = getErrorCode(error) === "T120";
      const context_0 = isVaultDataValidationError ? getErrorContext(error) : {};
      const errorMessage = context_0["id.phone_number"] ?? error;
      onError("collect-phone", errorMessage);
    };
    $[4] = getErrorCode;
    $[5] = getErrorContext;
    $[6] = onError;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = {
      mutationFn: t3,
      onError: t4
    };
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const continueMutation = useMutation(t5);
  const l10n = useL10nContext();
  let t6;
  if ($[11] !== isLive) {
    t6 = phone_0 => {
      const isPhoneValid = checkIsPhoneValid(phone_0, !isLive);
      if (!isPhoneValid) {
        logInfo("Phone number is not valid");
      }
      return isPhoneValid;
    };
    $[11] = isLive;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handlePhoneValidation = t6;
  let t7;
  if ($[13] !== continueMutation) {
    t7 = phoneFromForm => {
      continueMutation.mutate(phoneFromForm);
    };
    $[13] = continueMutation;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleSubmit = t7;
  let t8;
  if ($[15] !== continueMutation || $[16] !== onPrev) {
    t8 = onPrev ? () => {
      continueMutation.mutate(null, {
        onSuccess: onPrev
      });
    } : undefined;
    $[15] = continueMutation;
    $[16] = onPrev;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleGoBack = t8;
  const t9 = defaultValues.phoneNumber;
  const t10 = email?.value;
  let t11;
  if ($[18] !== handleGoBack || $[19] !== logoConfig) {
    t11 = getHeader(logoConfig, handleGoBack);
    $[18] = handleGoBack;
    $[19] = logoConfig;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = continueMutation.isPending;
  const t13 = continueMutation.isPending ? noop : handleSubmit;
  let t14;
  if ($[21] !== t) {
    t14 = t(_temp);
    $[21] = t;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t) {
    t15 = t(_temp2);
    $[23] = t;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t) {
    t16 = t(_temp3);
    $[25] = t;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t) {
    t17 = t(_temp4);
    $[27] = t;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t) {
    t18 = t(_temp5);
    $[29] = t;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t) {
    t19 = t(_temp6);
    $[31] = t;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t) {
    t20 = t(_temp7);
    $[33] = t;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t14 || $[36] !== t15 || $[37] !== t16 || $[38] !== t17 || $[39] !== t18 || $[40] !== t19 || $[41] !== t20) {
    t21 = {
      headerTitle: t14,
      headerSubtitle: t15,
      cta: t16,
      emailChangeCta: t17,
      phoneInvalid: t18,
      phoneLabel: t19,
      phoneRequired: t20
    };
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== verifiedPrefillContext) {
    t22 = <VerifiedPrefillFooter context={verifiedPrefillContext} />;
    $[43] = verifiedPrefillContext;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== children || $[46] !== t22) {
    t23 = <>{children}{t22}</>;
    $[45] = children;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== continueMutation.isPending || $[49] !== defaultValues.phoneNumber || $[50] !== handleGoBack || $[51] !== handlePhoneValidation || $[52] !== l10n || $[53] !== t10 || $[54] !== t11 || $[55] !== t13 || $[56] !== t21 || $[57] !== t23) {
    t24 = <PhonePageStructure countries={COUNTRIES} defaultPhone={t9} email={t10} Header={t11} isLoading={t12} l10n={l10n} onChangeEmailClick={handleGoBack} onSubmit={t13} phoneValidator={handlePhoneValidation} texts={t21} Footer={t23} />;
    $[48] = continueMutation.isPending;
    $[49] = defaultValues.phoneNumber;
    $[50] = handleGoBack;
    $[51] = handlePhoneValidation;
    $[52] = l10n;
    $[53] = t10;
    $[54] = t11;
    $[55] = t13;
    $[56] = t21;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  return t24;
};
const VerifiedPrefillFooter = t0 => {
  const $ = _c(4);
  const {
    context
  } = t0;
  if (!context?.isEnabled) {
    return null;
  }
  const collectsSsn = context.collectsSsn;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Divider />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <PoweredByVerified />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== collectsSsn) {
    t3 = <div className="flex flex-col items-center justify-center gap-8">{t1}<div className="flex flex-col gap-4">{t2}<p className="text-caption-2 text-tertiary text-center"><VerifiedPrefillDisclaimer collectsSsn={collectsSsn} /></p></div></div>;
    $[2] = collectsSsn;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
export default CollectPhone;
function _temp($) {
  return $["phone-step"].title;
}
function _temp2($_0) {
  return $_0["phone-step"].subtitle;
}
function _temp3($_1) {
  return $_1.continue;
}
function _temp4($_2) {
  return $_2.change;
}
function _temp5($_3) {
  return $_3["phone-step"].form["input-invalid"];
}
function _temp6($_4) {
  return $_4["phone-number"];
}
function _temp7($_5) {
  return $_5["phone-step"].form["input-required"];
}